import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock, Menu, X } from 'lucide-react';
import { useLang } from '@/i18n/LanguageContext';
import Logo from '@/components/Logo';
import LanguageSelector from '@/components/LanguageSelector';

export default function Header() {
  const { content, route } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled || open ? 'border-b border-brand-line bg-white/90 shadow-[0_8px_30px_-18px_rgba(10,36,64,0.35)] backdrop-blur-xl' : 'bg-transparent'}`}
    >
      <div className="container-x flex h-[76px] items-center justify-between gap-3">
        <Link to={route('home')} data-testid="header-logo-link" aria-label="Rodotrack — início" className="shrink-0">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-5 xl:gap-6 lg:flex">
          {content.nav.map((item) => {
            const active = pathname === route(item.k);
            return (
              <Link
                key={item.k}
                to={route(item.k)}
                data-testid={`header-nav-${item.k}`}
                className={`relative text-[13.5px] font-medium transition-colors ${active ? 'text-brand-blue' : 'text-brand-navy/80 hover:text-brand-blue'}`}
              >
                {item.label}
                {active && <span className="absolute -bottom-1.5 left-0 h-[2px] w-full rounded-full bg-brand-blue" />}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSelector />
          <a
            href={content.platformUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-client-area-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy px-4 py-2 text-[13px] font-semibold text-white shadow-[0_10px_24px_-10px_rgba(10,36,64,0.8)] transition hover:bg-brand-blue"
          >
            <Lock className="h-3.5 w-3.5 text-brand-cyan" />
            <span className="hidden sm:inline">{content.header.clientArea}</span>
          </a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="header-hamburger-btn"
            aria-label="Abrir menu"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-line text-brand-navy lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="absolute inset-x-0 top-full border-b border-brand-line bg-white shadow-xl lg:hidden">
          <nav className="container-x flex flex-col py-4">
            {content.nav.map((item) => (
              <Link
                key={item.k}
                to={route(item.k)}
                data-testid={`mobile-nav-${item.k}`}
                className={`border-b border-brand-line/70 py-3.5 font-display text-lg font-semibold ${pathname === route(item.k) ? 'text-brand-blue' : 'text-brand-navy'}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}